import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Radio, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useUserData } from './UserDataContext';
import { RoomService } from '../services/RoomService';
import {
    ROOM_MAX_DURATION_MINUTES,
    ROOM_MIN_DURATION_MINUTES,
    ROOM_MIN_MEMBERS,
    ROOM_QUESTION_MAX_LENGTH,
    type RoomJoinPolicy,
} from '../services/roomLifecycle';

interface CreateRoomModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (roomId: string) => void;
}

// Hours. 48 is offered as "2 days" — a room that spans more than one sitting is
// a different kind of thing from one that runs for an afternoon, and labelling it
// 48h buries that.
const DURATION_PRESETS = [1, 2, 4, 8, 12, 24, 48];

/** "2 days" reads better than "48h" once a room outlives a single day. */
function durationLabel(h: number): string {
    return h >= 24 && h % 24 === 0 ? `${h / 24}d` : `${h}h`;
}

/**
 * Start a room: title, rules, and how long it runs.
 *
 * Mods are deliberately not chosen here. Nobody is in the room yet, so there is
 * no one to appoint — the room info menu handles it once people arrive.
 */
export function CreateRoomModal({ isOpen, onClose, onCreated }: CreateRoomModalProps) {
    const { userProfile, firebaseUser } = useUserData();
    const [title, setTitle] = useState('');
    const [rules, setRules] = useState('');
    const [hours, setHours] = useState(2);
    // How people get in. 'approval' is the default because it is what every room
    // did before this existed — an existing host's habits keep working.
    const [joinPolicy, setJoinPolicy] = useState<RoomJoinPolicy>('approval');
    const [joinQuestion, setJoinQuestion] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setTitle('');
            setRules('');
            setHours(2);
            setSubmitting(false);
        }
    }, [isOpen]);

    const handleCreate = async () => {
        // The auth uid, not userProfile.id: the security rules compare against
        // request.auth.uid, and on a merged account those two can differ.
        const uid = String(firebaseUser?.uid || userProfile?.id || '');
        if (!uid) {
            toast.error('You need to be signed in to host a room.');
            return;
        }
        setSubmitting(true);
        try {
            const roomId = await RoomService.createRoom(uid, {
                title,
                rules,
                durationMinutes: hours * 60,
                joinPolicy,
                joinQuestion,
            });
            toast.success('Room created', {
                description: `It goes live once ${ROOM_MIN_MEMBERS} people are in.`,
            });
            onCreated(roomId);
        } catch (e: any) {
            toast.error(e?.message || 'Could not create the room.');
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0"
                        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    />
                    <motion.div
                        data-tour="guide-create-room-modal"
                        initial={{ scale: 0.94, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.94, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col bg-white dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 shadow-2xl"
                        style={{ maxHeight: '86vh' }}
                    >
                        <div className="p-4 flex items-center justify-between border-b border-black/10 dark:border-white/10 bg-[#f2deb3] dark:bg-[#121212]">
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ background: 'rgba(68,120,234,0.15)' }}
                                >
                                    <Radio className="w-5 h-5" style={{ color: '#4478ea' }} />
                                </div>
                                <div>
                                    <h2 className="text-lg text-black dark:text-white" style={{ fontWeight: 700 }}>
                                        Start a Room
                                    </h2>
                                    <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 11 }}>
                                        Goes live at {ROOM_MIN_MEMBERS} members, then counts down
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="w-9 h-9 p-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
                            >
                                <X className="w-4 h-4 text-black dark:text-white" />
                            </Button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 space-y-4">
                            <div>
                                <label
                                    className="block text-black dark:text-white"
                                    style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                                >
                                    Title
                                </label>
                                <Input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value.slice(0, 80))}
                                    placeholder="What is this room about?"
                                    className="bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-xl"
                                />
                                <p className="text-[#666666] dark:text-[#a0a0a0] mt-1" style={{ fontSize: 11 }}>
                                    {title.trim().length}/80
                                </p>
                            </div>

                            <div>
                                <label
                                    className="block text-black dark:text-white"
                                    style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                                >
                                    Rules
                                </label>
                                <Textarea
                                    value={rules}
                                    onChange={(e) => setRules(e.target.value.slice(0, 1000))}
                                    placeholder="How should people behave in here? (optional)"
                                    className="bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-xl" style={{ minHeight: 96 }}
                                />
                                <p className="text-[#666666] dark:text-[#a0a0a0] mt-1" style={{ fontSize: 11 }}>
                                    Kept in the room info menu, not pinned to the chat.
                                </p>
                            </div>

                            <div>
                                <label
                                    className="block text-black dark:text-white"
                                    style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                                >
                                    Runs for
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {DURATION_PRESETS.map((h) => (
                                        <button
                                            key={h}
                                            onClick={() => setHours(h)}
                                            className={`px-3 py-2 rounded-xl transition-all ${
                                                hours === h
                                                    ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                                                    : 'bg-black/5 dark:bg-white/5 text-black/60 dark:text-white/60'
                                            }`}
                                            style={{ fontSize: 13, fontWeight: 700 }}
                                        >
                                            {durationLabel(h)}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[#666666] dark:text-[#a0a0a0] mt-2" style={{ fontSize: 11 }}>
                                    The clock only starts once the room goes live. You can extend it
                                    once, up to {durationLabel(ROOM_MAX_DURATION_MINUTES / 60)} total.
                                </p>
                            </div>
                            {/* Who gets in */}
                            <div>
                                <label
                                    className="block text-black dark:text-white"
                                    style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                                >
                                    Who can join
                                </label>
                                <div className="flex flex-col gap-2">
                                    {([
                                        ['approval', 'I approve each person', 'You see each request and let people in yourself.'],
                                        ['question', 'They answer a question', 'You set a question. Their answer is shown to you before you decide.'],
                                        ['open', 'Anyone can join', 'No approval step. People come and go freely.'],
                                    ] as [RoomJoinPolicy, string, string][]).map(([value, label, hint]) => (
                                        <button
                                            key={value}
                                            onClick={() => setJoinPolicy(value)}
                                            className={`w-full text-left px-3 py-2 rounded-xl transition-all ${
                                                joinPolicy === value
                                                    ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                                                    : 'bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70'
                                            }`}
                                        >
                                            <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
                                            <span
                                                className="block"
                                                style={{ fontSize: 11, opacity: joinPolicy === value ? 0.85 : 0.6 }}
                                            >
                                                {hint}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {joinPolicy === 'question' && (
                                    <div className="mt-2">
                                        <input
                                            value={joinQuestion}
                                            onChange={(e) => setJoinQuestion(e.target.value.slice(0, ROOM_QUESTION_MAX_LENGTH))}
                                            placeholder="e.g. Who told you about this room?"
                                            className="w-full rounded-xl bg-black/5 dark:bg-white/5 text-black dark:text-white px-3 py-2 outline-none"
                                            style={{ fontSize: 13 }}
                                        />
                                        <p className="text-[#666666] dark:text-[#a0a0a0] mt-1 text-right" style={{ fontSize: 10 }}>
                                            {joinQuestion.length}/{ROOM_QUESTION_MAX_LENGTH}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-black/10 dark:border-white/10 dark:bg-[#121212]">
                            <Button
                                onClick={handleCreate}
                                disabled={submitting || title.trim().length < 3}
                                className="w-full h-11 rounded-xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md disabled:opacity-50"
                                style={{ fontWeight: 700 }}
                            >
                                {submitting ? (
                                    <span className="flex items-center gap-2">
                                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><Loader2 className="w-4 h-4" /></motion.div> Creating...
                                    </span>
                                ) : (
                                    `Start ${durationLabel(hours)} Room`
                                )}
                            </Button>
                            <p
                                className="text-center text-[#666666] dark:text-[#a0a0a0] mt-2"
                                style={{ fontSize: 11 }}
                            >
                                One room at a time. Everything in it is deleted when it ends.
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
}

export default CreateRoomModal;
